import { BOOKED_STATUSES } from "../../../api/client-sessions-summary/constants";
import { DASHBOARD_COLORS } from "../../../dashboard-framework/colors";
import { CLIENT_TYPE_OPTIONS, EXCLUDE_INTERNAL_SESSIONS_FILTER, sessionClientTypeFilterByOption } from "../../../dashboard-framework/commonFilters";
import { AggregateQuerySpec, GlobalFilterConfig, KpiCellConfig } from "../../../dashboard-framework/types";
import {
    CLINIC_OPTIONS,
    DONE_AND_TO_COME_STATUSES,
    NFB_PSY_SERVICES,
    TREATMENT_FIRST_SESSION_STARTED_OR_TO_COME_STATUSES,
    treatmentsFirstSessionQueryForStatuses,
} from "../shared/sharedMetrics";
import { FIRST_EEG_SERVICE, GENERAL_EEG_SERVICE, NFB_SERVICE, PSY_SERVICE, REPORTS_FIRST_SERVICE } from "../../../utils/constants";

// Fórmulas validadas contra os widgets Zoho em 2026-06-10: avaliações = serviço 39; outros EEG = 35;
// consultas de avaliação = serviço 37; canceladas NFB/Psi incluem "missed"; sessões internas excluídas;
// semana = ISO completa com realizadas + por vir; indisponibilidade por start_date no período;
// tratamentos iniciados = primeira sessão NFB/Psi já iniciada — realizada/à espera na clínica/em sessão —
// no período (treatmentsFirstStartedSessionQuery);
// "+ por vir" = primeira sessão NFB/Psi iniciada OU ainda por vir (marcada/confirmada) no período
// (treatmentsFirstStartedOrToComeSessionQuery — específico deste dashboard, NÃO partilhado com o Marketing);
// clínica virtual (id 2) excluída de todas as métricas (sessões de teste).

export const DAILY_ANALYSIS_KEY = "daily-analysis";

const EVALUATION_CONSULTATION_SERVICES = [REPORTS_FIRST_SERVICE];
const EVALUATION_SERVICES = [FIRST_EEG_SERVICE];
const EEG_OTHERS_SERVICES = [GENERAL_EEG_SERVICE];
const TO_COME_STATUSES = BOOKED_STATUSES;

// Clínica "Virtual - Fora da clínica" (id 2): sessões de teste — excluídas de todas as métricas deste
// dashboard. O `_or` com `_null` preserva sessões sem clínica (um `_neq` sozinho descartaria as NULL).
export const VIRTUAL_CLINIC_ID = 2;
export const EXCLUDE_VIRTUAL_CLINIC_FILTER = {
    _or: [{ clinic_id: { _neq: VIRTUAL_CLINIC_ID } }, { clinic_id: { _null: true } }],
};
export const DAILY_ANALYSIS_CLINIC_OPTIONS = CLINIC_OPTIONS.filter((option) => option.value !== VIRTUAL_CLINIC_ID);

export const dailyAnalysisGlobalFilters: Array<GlobalFilterConfig> = [
    { key: "timeline", type: "datePreset", label: "Período", field: "start_date", defaultPreset: "este_mes" },
    { key: "clinic", type: "select", label: "Clínica", field: "clinic_id", options: DAILY_ANALYSIS_CLINIC_OPTIONS },
];

export const sessionsByStatusAndServiceQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    groupBy: ["status", "service"],
    fixedFilter: { _and: [EXCLUDE_INTERNAL_SESSIONS_FILTER, EXCLUDE_VIRTUAL_CLINIC_FILTER] },
    dataScope: "global",
};

// "Tratamentos Iniciados" (primeira sessão já arrancou — realizada, à espera na clínica ou em sessão)
// vive em sharedMetrics (treatmentsFirstStartedSessionQuery), partilhado com o Marketing Digital.
// "Tratamentos Iniciados + Por Vir": à fórmula de "iniciado" juntam-se as sessões ainda por vir
// (marcada/confirmada). Assim o tratamento aparece desde que é marcado e mantém-se enquanto o cliente
// está na clínica na primeira sessão.
export const treatmentsFirstStartedOrToComeSessionQuery = treatmentsFirstSessionQueryForStatuses(TREATMENT_FIRST_SESSION_STARTED_OR_TO_COME_STATUSES);

export { CLIENT_TYPE_OPTIONS };
export const clientTypeFilterByOption = sessionClientTypeFilterByOption;

export const weeklySessionsByServiceQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    groupBy: ["service"],
    fixedFilter: {
        _and: [
            { status: { _in: DONE_AND_TO_COME_STATUSES } },
            { service: { _in: NFB_PSY_SERVICES } },
            EXCLUDE_INTERNAL_SESSIONS_FILTER,
            EXCLUDE_VIRTUAL_CLINIC_FILTER,
        ],
    },
    dataScope: "global",
};

export const dailyAnalysisKpiCells: Array<KpiCellConfig> = [
    { key: "evaluationsDone", label: "Avaliações Realizadas", match: { statuses: ["done"], services: EVALUATION_SERVICES } },
    { key: "evaluationsToCome", label: "Avaliações Por Vir", match: { statuses: TO_COME_STATUSES, services: EVALUATION_SERVICES } },
    { key: "reportsDone", label: "Consultas de Avaliação Realizadas", match: { statuses: ["done"], services: EVALUATION_CONSULTATION_SERVICES } },
    { key: "nfbDone", label: "Neurofeedback Realizadas", match: { statuses: ["done"], services: [NFB_SERVICE] } },
    { key: "psyDone", label: "Psicologia Realizadas", match: { statuses: ["done"], services: [PSY_SERVICE] } },
    { key: "eegOthersDone", label: "Outros EEG Realizados", match: { statuses: ["done"], services: EEG_OTHERS_SERVICES } },
    { key: "nfbToCome", label: "Neurofeedback Por Vir", match: { statuses: TO_COME_STATUSES, services: [NFB_SERVICE] } },
    { key: "psyToCome", label: "Psicologia Por Vir", match: { statuses: TO_COME_STATUSES, services: [PSY_SERVICE] } },
    { key: "evaluationsCancelled", label: "Avaliações Canceladas", match: { statuses: ["cancelled"], services: EVALUATION_SERVICES } },
    { key: "staffUnavailable", label: "Indisponibilidade de Staff", match: { statuses: ["staff_unavailable"] } },
    { key: "nfbCancelled", label: "Neurofeedback Canceladas", match: { statuses: ["cancelled", "missed"], services: [NFB_SERVICE] } },
    { key: "psyCancelled", label: "Psicologia Canceladas", match: { statuses: ["cancelled", "missed"], services: [PSY_SERVICE] } },
    { key: "evaluationsDonePlusToCome", label: "Avaliações Realizadas + Por Vir", sumOfCells: ["evaluationsDone", "evaluationsToCome"] },
    {
        key: "totalSessionsDone",
        label: "Total Sessões Realizadas",
        sumOfCells: ["nfbDone", "psyDone"],
        businessNote: "Os bónus mensais da equipa baseiam-se neste total.",
    },
    { key: "totalToCome", label: "Total Por Vir", sumOfCells: ["nfbToCome", "psyToCome"] },
    { key: "totalDonePlusToCome", label: "Total Realizadas + Por Vir", sumOfCells: ["totalSessionsDone", "totalToCome"] },
    { key: "totalCancelled", label: "Total Canceladas", sumOfCells: ["nfbCancelled", "psyCancelled"] },
];

type KpiCellPlacement = { cellKey: string; color: string; span?: number };

type KpiRowConfig = {
    key: string;
    color: string;
    cellKeys?: Array<string>;
    // Linhas de cor mista (ex.: "Outros EEG" cinzento + "Por Vir" azul na mesma linha) usam `cells`.
    cells?: Array<KpiCellPlacement>;
    gridTemplateColumns?: string;
};

// Réplica do layout Zoho numa grelha de 12 colunas: "Outros EEG Realizados" (cinzento) ocupa metade da
// linha (6 colunas = 3 cartões) à esquerda, partilhando-a com os 3 cartões azuis "Por Vir" (2 colunas cada)
// à direita. A linha de cancelados alinha por baixo: as 2 da esquerda (3 colunas cada) sob o cinzento e as
// 3 da direita (2 colunas cada) sob os azuis.
export const dailyAnalysisKpiRows: Array<KpiRowConfig> = [
    {
        key: "summary",
        color: DASHBOARD_COLORS.doneDark,
        cellKeys: ["evaluationsDonePlusToCome", "totalDonePlusToCome"],
    },
    {
        key: "done",
        color: DASHBOARD_COLORS.done,
        cellKeys: ["evaluationsDone", "evaluationsToCome", "reportsDone", "nfbDone", "psyDone", "totalSessionsDone"],
    },
    {
        key: "othersAndToCome",
        color: DASHBOARD_COLORS.toCome,
        gridTemplateColumns: "repeat(12, 1fr)",
        cells: [
            { cellKey: "eegOthersDone", color: DASHBOARD_COLORS.neutral, span: 6 },
            { cellKey: "nfbToCome", color: DASHBOARD_COLORS.toCome, span: 2 },
            { cellKey: "psyToCome", color: DASHBOARD_COLORS.toCome, span: 2 },
            { cellKey: "totalToCome", color: DASHBOARD_COLORS.toCome, span: 2 },
        ],
    },
    {
        key: "cancelled",
        color: DASHBOARD_COLORS.cancelled,
        gridTemplateColumns: "repeat(12, 1fr)",
        cells: [
            { cellKey: "evaluationsCancelled", color: DASHBOARD_COLORS.cancelled, span: 3 },
            { cellKey: "staffUnavailable", color: DASHBOARD_COLORS.cancelled, span: 3 },
            { cellKey: "nfbCancelled", color: DASHBOARD_COLORS.cancelled, span: 2 },
            { cellKey: "psyCancelled", color: DASHBOARD_COLORS.cancelled, span: 2 },
            { cellKey: "totalCancelled", color: DASHBOARD_COLORS.cancelled, span: 2 },
        ],
    },
];

export const evaluationsDoneGaugeMatch = { statuses: ["done"], services: EVALUATION_SERVICES };
export const totalSessionsDoneGaugeMatch = { statuses: ["done"], services: NFB_PSY_SERVICES };

export const dailyAnalysisGaugeGoals: Record<string, number> = {
    evaluationsDone: 110,
    totalSessionsDone: 1692,
};

// Cores das clínicas (campo "color" da coleção clinics no Directus), usadas para tingir as linhas do
// drill-down "Tratamentos Iniciados + Por Vir" por clínica da 1ª sessão.
export const CLINIC_ROW_COLORS: Record<string, string> = {
    Porto: "#2057C5",
    Braga: "#D11515",
    "Virtual - Fora da clínica": "#D750F2",
};
